/**
 * M4 aiDialogueSettings の Firestore ローダー (Sprint BG-DataSettings 18)。
 *
 * 旧 `old/apps/user/src/lib/aiDialogueLoader.ts` (L4 参照、docs/97 §3 移植ログ) を v2 仕様に
 * 縮減して踏襲。
 *
 * 踏襲点:
 *   - settingsId → M4 (aiDialogueSettings/{id}) 単発取得
 *   - 結果は module-scope Map cache に保持 (open/close で毎回 fetch しない)
 *   - 404 / fetch error は null 返却 (UI 側 fallback prompt で動作継続)
 *
 * 変更点 (v2):
 *   - 旧の `DistrictAiDialogueConfig`/`resolveStepEnabled`/`stepEnabledOverride` 機構は v2 で
 *     未採用 (district 直下に `aiDialogueSettingsId` のみ expose)。本ローダーは「指定された ID の
 *     M4 マスターを読み出すだけ」の素朴な責務に縮減。
 *   - v2 `AiDialogueSettings` は `steps: { stepId, prompt, enabled }[]` で構成され、stepEnabled
 *     合成ロジックは callable / generateEcwDialogue 側に委譲 (Sprint X5g の設計踏襲)。
 *   - useDistrict() 経由で district.aiDialogueSettingsId を取得して本 hook に渡す pattern。
 *
 * 廃止点:
 *   - 旧 `DistrictAiDialogueConfig.source='hardcoded'` 分岐 → v2 では M4 一本化 (docs/04 §3-3 M1 補足)
 *   - 旧 `step0Question` 抽出 → v2 では step.stepId === 'step0' の prompt を caller 側で読む
 *
 * 関連 docs: docs/91 U-L11 (M4 resolver) / docs/04 §3-1 M4。
 */
import { doc, getDoc } from 'firebase/firestore';
import type { AiDialogueSettings } from '@citizen-walk/shared-types';
import { db } from '../../lib/firebase';

export interface AiDialogueLoadResult {
  settings: AiDialogueSettings | null;
}

const _cache = new Map<string, AiDialogueLoadResult>();

/**
 * settingsId から M4 aiDialogueSettings/{id} を取得。
 * settingsId が空 / null の場合は null を返す (caller は fallback prompt で動作継続)。
 */
export async function loadAiDialogueSettings(
  settingsId: string | null | undefined,
): Promise<AiDialogueLoadResult> {
  if (!settingsId) return { settings: null };
  const cached = _cache.get(settingsId);
  if (cached) return cached;

  try {
    const snap = await getDoc(doc(db, 'aiDialogueSettings', settingsId));
    if (!snap.exists()) {
      const result: AiDialogueLoadResult = { settings: null };
      _cache.set(settingsId, result);
      return result;
    }
    const settings = {
      id: settingsId,
      ...(snap.data() as Omit<AiDialogueSettings, 'id'>),
    } as AiDialogueSettings;
    const result: AiDialogueLoadResult = { settings };
    _cache.set(settingsId, result);
    return result;
  } catch (e) {
    console.warn('Failed to load AI dialogue settings:', e);
    return { settings: null };
  }
}

/**
 * 指定 stepId の prompt を取得 (見つからなければ null)。
 * 旧 `step0Question` 抽出ロジックの汎用版。
 */
export function getDialogueStepPrompt(
  settings: AiDialogueSettings | null,
  stepId: string,
): string | null {
  if (!settings) return null;
  const step = settings.steps.find((s) => s.stepId === stepId);
  if (!step || !step.enabled) return null;
  const prompt = step.prompt?.trim() ?? '';
  return prompt.length > 0 ? prompt : null;
}

/** 全 step が enabled かを { [stepId]: boolean } で返す。 */
export function getDialogueStepEnabledMap(
  settings: AiDialogueSettings | null,
): Record<string, boolean> {
  if (!settings) return {};
  const out: Record<string, boolean> = {};
  for (const step of settings.steps) {
    out[step.stepId] = step.enabled;
  }
  return out;
}

/** test 用 cache clear (production code からは呼ばない)。 */
export function _clearAiDialogueLoaderCache(): void {
  _cache.clear();
}
